import levelsData from "../../generated/levels.json" with { type: "json" };
import {
  BRUTEFORCE_CONDITION_TYPE_LABELS,
  BRUTEFORCE_CONDITION_TYPES,
  BRUTEFORCE_DIRECTION_IDS,
  BRUTEFORCE_DIRECTION_LABELS,
  BRUTEFORCE_METRICS,
  BRUTEFORCE_MUTATION_ACTIONS,
  DEFAULT_BRUTEFORCE_CONDITION,
  DEFAULT_BRUTEFORCE_SETTINGS,
  getBruteforceMetric,
  normalizeBruteforceCondition,
  normalizeBruteforceSettings,
} from "./defaults.js";
import { BruteforceRunner } from "./runner.js";
import { formatScriptFrame } from "./script-format.js";

export class BruteforceSessionController {
  constructor({
    sessionId,
    initialSettings = null,
    getLevelId,
    getSpikesKill,
    onSettingsChange,
    onRemove,
  }) {
    this.sessionId = sessionId;
    this.getLevelId = getLevelId;
    this.getSpikesKill = getSpikesKill;
    this.onSettingsChange = onSettingsChange;
    this.onRemove = onRemove;
    this.runner = new BruteforceRunner({
      onUpdate: (status) => this.handleRunnerUpdate(status),
    });
    this.status = this.runner.getStatus();
    this.settings = normalizeBruteforceSettings(initialSettings ?? DEFAULT_BRUTEFORCE_SETTINGS);
    this.conditionRows = [];
    this.mutationFieldGroups = {};
    this.activeMutationKey = "modify";
    this.selectedMutationRange = null;
    this.isSelectingMutationCells = false;
    this.handleDocumentMouseUp = () => {
      this.isSelectingMutationCells = false;
    };
    this.root = null;
    this.titleElement = null;
  }

  setSessionId(sessionId) {
    this.sessionId = sessionId;
    if (this.titleElement) {
      this.titleElement.textContent = `Bruteforce Session ${this.sessionId}`;
    }
  }

  render() {
    const panel = document.createElement("section");
    panel.className = "bruteforce-panel";
    panel.addEventListener("copy", (event) => this.handleMutationMatrixCopy(event));
    panel.addEventListener("paste", (event) => this.handleMutationMatrixPaste(event));
    document.addEventListener("mouseup", this.handleDocumentMouseUp);

    const heading = document.createElement("div");
    heading.className = "bruteforce-heading";

    const titleRow = document.createElement("div");
    titleRow.className = "bruteforce-session-title-row";

    const title = document.createElement("span");
    title.className = "input-title";
    title.textContent = `Bruteforce Session ${this.sessionId}`;
    this.titleElement = title;

    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.className = "btn btn-small";
    removeButton.textContent = "🗑";
    removeButton.title = "Remove session";
    removeButton.setAttribute("aria-label", "Remove session");
    removeButton.addEventListener("click", () => {
      this.stop();
      this.onRemove(this);
    });

    titleRow.append(title, removeButton);

    const copy = document.createElement("p");
    copy.className = "bruteforce-copy";
    copy.textContent = "Detached bruteforce session with its own input script and settings.";
    heading.append(titleRow, copy);

    const createField = ({ title: labelText, description = "", inputType = "text", min = null, step = null, value = "", options = null }) => {
      const section = document.createElement("label");
      section.className = "bruteforce-section";

      const label = document.createElement("span");
      label.className = "input-title";
      label.textContent = labelText;

      const help = document.createElement("span");
      help.className = "bruteforce-help";
      help.textContent = description;

      let field;
      if (options) {
        field = document.createElement("select");
        for (const option of options) {
          const element = document.createElement("option");
          element.value = option.value;
          element.textContent = option.label;
          field.appendChild(element);
        }
      } else {
        field = document.createElement("input");
        field.type = inputType;
        if (min != null) {
          field.min = String(min);
        }
        if (step != null) {
          field.step = String(step);
        }
      }
      field.className = "bruteforce-field";
      field.value = String(value);
      section.append(label, help, field);
      return { section, field };
    };

    const scriptSection = document.createElement("label");
    scriptSection.className = "bruteforce-section bruteforce-block bruteforce-block-script";
    const scriptLabel = document.createElement("span");
    scriptLabel.className = "input-title";
    scriptLabel.textContent = "Session Inputs";
    const scriptHelp = document.createElement("span");
    scriptHelp.className = "bruteforce-help";
    scriptHelp.textContent = "This script is the session-local seed and is updated when a better run is found.";
    this.scriptField = document.createElement("textarea");
    this.scriptField.className = "script-field bruteforce-script-field";
    this.scriptField.spellcheck = false;
    this.scriptField.value = this.settings.scriptText ?? "";
    this.scriptField.addEventListener("input", () => this.persistSettings());
    scriptSection.append(scriptLabel, scriptHelp, this.scriptField);

    const targetSection = document.createElement("div");
    targetSection.className = "bruteforce-grid bruteforce-block bruteforce-block-target";

    const targetBanner = document.createElement("div");
    targetBanner.className = "bruteforce-block-banner";
    targetBanner.textContent = "Goal + Constraints";

    const levelField = createField({
      title: "Level",
      description: "Pick the level this session bruteforces.",
      options: levelsData.levels.map((level) => ({ value: String(level.id), label: `Level ${level.id}` })),
      value: this.settings.levelId,
    });
    this.levelField = levelField.field;

    const targetMetric = createField({
      title: "Target Metric",
      description: "Choose the value to optimize.",
      options: BRUTEFORCE_METRICS.map((metric) => ({ value: metric.id, label: metric.label })),
      value: this.settings.target.metric,
    });
    this.targetMetricField = targetMetric.field;

    const targetDirection = createField({
      title: "Goal",
      description: "Increase, decrease, or converge toward a value.",
      options: Object.values(BRUTEFORCE_DIRECTION_IDS).map((direction) => ({
        value: direction,
        label: BRUTEFORCE_DIRECTION_LABELS[direction],
      })),
      value: this.settings.target.direction,
    });
    this.targetDirectionField = targetDirection.field;

    const targetTime = createField({
      title: "Target Time",
      description: "Use seconds:frames, like 3:4.",
      value: this.settings.targetTime,
    });
    this.targetTimeField = targetTime.field;

    const targetValue = createField({
      title: "Converge To",
      description: "Only used when Goal is Converge.",
      inputType: "number",
      step: "any",
      value: this.settings.target.value,
    });
    this.targetValueField = targetValue.field;
    this.targetValueSection = targetValue.section;
    this.targetValueSection.hidden = this.settings.target.direction !== BRUTEFORCE_DIRECTION_IDS.CONVERGE;

    const conditionsSection = document.createElement("div");
    conditionsSection.className = "bruteforce-section bruteforce-section-full";
    const conditionsHeading = document.createElement("div");
    conditionsHeading.className = "input-title";
    conditionsHeading.textContent = "Conditions";
    const conditionsHelp = document.createElement("p");
    conditionsHelp.className = "bruteforce-help";
    conditionsHelp.textContent = "Add any number of lower/upper bounds on any tracked metric.";
    this.conditionsEmpty = document.createElement("p");
    this.conditionsEmpty.className = "bruteforce-help";
    this.conditionsEmpty.textContent = "No extra conditions.";
    this.conditionsList = document.createElement("div");
    this.conditionsList.className = "bruteforce-conditions-list";
    const addConditionButton = document.createElement("button");
    addConditionButton.type = "button";
    addConditionButton.className = "btn btn-small";
    addConditionButton.textContent = "Add Condition";
    addConditionButton.addEventListener("click", () => {
      this.conditionRows.push({ ...DEFAULT_BRUTEFORCE_CONDITION });
      this.renderConditions();
      this.persistSettings();
    });
    conditionsSection.append(conditionsHeading, conditionsHelp, this.conditionsEmpty, this.conditionsList, addConditionButton);
    this.conditionRows = this.settings.conditions.map((condition) => normalizeBruteforceCondition(condition));

    targetSection.append(targetBanner, levelField.section, targetMetric.section, targetDirection.section, targetTime.section, targetValue.section, conditionsSection);

    const optimizationSection = document.createElement("div");
    optimizationSection.className = "bruteforce-grid bruteforce-grid-wide bruteforce-block bruteforce-block-mutation";

    const optimizationBanner = document.createElement("div");
    optimizationBanner.className = "bruteforce-block-banner";
    optimizationBanner.textContent = "Mutation Controls";

    const mutationTabs = document.createElement("div");
    mutationTabs.className = "bruteforce-mutation-tabs bruteforce-section-full";
    this.mutationTabButtons = {};

    const mutationPanels = document.createElement("div");
    mutationPanels.className = "bruteforce-mutation-panels bruteforce-section-full";
    this.mutationPanels = {};

    for (const [key, title, description, settings, options] of [
      ["modify", "Modify", "Shift existing matching inputs inside the allowed time window.", this.settings.mutations.modify, { includeDelta: true }],
      ["add", "Add", "Insert new events inside the allowed time window.", this.settings.mutations.add, {}],
      ["delete", "Delete", "Remove matching existing events inside the allowed time window.", this.settings.mutations.delete, {}],
    ]) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "btn btn-small bruteforce-mutation-tab";
      button.setAttribute("role", "tab");
      button.setAttribute("aria-selected", "false");
      const tabEyebrow = document.createElement("span");
      tabEyebrow.className = "bruteforce-mutation-tab-eyebrow";
      tabEyebrow.textContent = "Mutation";
      const tabLabel = document.createElement("span");
      tabLabel.className = "bruteforce-mutation-tab-label";
      tabLabel.textContent = title;
      button.append(tabEyebrow, tabLabel);
      button.addEventListener("click", () => this.setActiveMutationSection(key));
      this.mutationTabButtons[key] = button;
      mutationTabs.appendChild(button);

      const panel = this.buildMutationSection(key, `${title} Inputs`, description, settings, options).section;
      this.mutationPanels[key] = panel;
      mutationPanels.appendChild(panel);
    }

    optimizationSection.append(optimizationBanner, mutationTabs, mutationPanels);

    const actionRow = document.createElement("div");
    actionRow.className = "row bruteforce-actions";
    this.startButton = document.createElement("button");
    this.startButton.type = "button";
    this.startButton.className = "btn";
    this.startButton.textContent = "Start Session";
    this.startButton.addEventListener("click", () => {
      void this.start();
    });

    this.stopButton = document.createElement("button");
    this.stopButton.type = "button";
    this.stopButton.className = "btn";
    this.stopButton.textContent = "Stop";
    this.stopButton.addEventListener("click", () => this.stop());

    actionRow.append(this.startButton, this.stopButton);

    this.statusBox = document.createElement("div");
    this.statusBox.className = "bruteforce-status bruteforce-block bruteforce-block-status";
    this.statusMessage = document.createElement("div");
    this.statusMessage.className = "bruteforce-status-line";
    this.iterationValue = document.createElement("div");
    this.iterationValue.className = "bruteforce-status-line";
    this.bestValue = document.createElement("div");
    this.bestValue.className = "bruteforce-status-line";
    this.statusBox.append(this.statusMessage, this.iterationValue, this.bestValue);

    for (const field of [
      this.levelField,
      this.targetMetricField,
      this.targetDirectionField,
      this.targetTimeField,
      this.targetValueField,
      ]) {
      field.addEventListener("input", () => this.persistSettings());
      field.addEventListener("change", () => this.persistSettings());
    }

    this.targetDirectionField.addEventListener("input", () => this.handleTargetDirectionChange());
    this.targetDirectionField.addEventListener("change", () => this.handleTargetDirectionChange());

    panel.append(heading, scriptSection, targetSection, optimizationSection, actionRow, this.statusBox);
    this.root = panel;
    this.renderConditions();
    this.setActiveMutationSection(this.activeMutationKey);
    this.updateView();
    return panel;
  }

  setActiveMutationSection(key) {
    this.activeMutationKey = key;
    for (const [entryKey, button] of Object.entries(this.mutationTabButtons ?? {})) {
      button.classList.toggle("is-active", entryKey === key);
      button.setAttribute("aria-selected", entryKey === key ? "true" : "false");
    }
    for (const [entryKey, panel] of Object.entries(this.mutationPanels ?? {})) {
      panel.hidden = entryKey !== key;
      panel.classList.toggle("is-active", entryKey === key);
      panel.style.position = entryKey === key ? "relative" : "absolute";
      panel.style.inset = entryKey === key ? "auto" : "0";
      panel.style.pointerEvents = entryKey === key ? "auto" : "none";
    }
  }

  handleTargetDirectionChange() {
    this.targetValueSection.hidden = this.targetDirectionField.value !== BRUTEFORCE_DIRECTION_IDS.CONVERGE;
    this.persistSettings();
  }

  buildConditionRow(condition, index) {
    const row = document.createElement("div");
    row.className = "bruteforce-condition-row";

    const metricField = document.createElement("select");
    metricField.className = "bruteforce-field";
    for (const metric of BRUTEFORCE_METRICS) {
      const option = document.createElement("option");
      option.value = metric.id;
      option.textContent = metric.shortLabel;
      metricField.appendChild(option);
    }
    metricField.value = condition.metric;

    const typeField = document.createElement("select");
    typeField.className = "bruteforce-field";
    for (const type of Object.values(BRUTEFORCE_CONDITION_TYPES)) {
      const option = document.createElement("option");
      option.value = type;
      option.textContent = BRUTEFORCE_CONDITION_TYPE_LABELS[type];
      typeField.appendChild(option);
    }
    typeField.value = condition.type;

    const valueField = document.createElement("input");
    valueField.type = "number";
    valueField.step = "any";
    valueField.className = "bruteforce-field";
    valueField.value = condition.value;

    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.className = "btn btn-small";
    removeButton.textContent = "🗑";
    removeButton.title = "Remove condition";
    removeButton.setAttribute("aria-label", "Remove condition");
    removeButton.addEventListener("click", () => {
      this.conditionRows.splice(index, 1);
      this.renderConditions();
      this.persistSettings();
    });

    for (const field of [metricField, typeField, valueField]) {
      field.addEventListener("input", () => this.persistSettings());
      field.addEventListener("change", () => this.persistSettings());
    }

    row.append(metricField, typeField, valueField, removeButton);
    return { row, metricField, typeField, valueField };
  }

  buildMutationSection(key, title, description, settings, { includeDelta = false } = {}) {
    const section = document.createElement("div");
    section.className = "bruteforce-section bruteforce-mutation-card bruteforce-section-full";

    const createActionIcon = (actionId) => {
      const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      icon.setAttribute("viewBox", "0 0 16 16");
      icon.setAttribute("aria-hidden", "true");
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "currentColor");
      path.setAttribute("stroke-linecap", "round");
      path.setAttribute("stroke-linejoin", "round");
      path.setAttribute("stroke-width", "1.8");
      switch (actionId) {
        case "left":
          path.setAttribute("d", "M13 8H3M7 4 3 8l4 4");
          break;
        case "right":
          path.setAttribute("d", "M3 8h10M9 4l4 4-4 4");
          break;
        default:
          path.setAttribute("d", "M8 13V3M4 7l4-4 4 4");
          break;
      }
      icon.appendChild(path);
      return icon;
    };

    const heading = document.createElement("span");
    heading.className = "input-title";
    heading.textContent = title;

    const help = document.createElement("span");
    help.className = "bruteforce-help";
    help.textContent = description;

    const matrix = document.createElement("div");
    matrix.className = "bruteforce-mutation-matrix";

    const corner = document.createElement("div");
    corner.className = "bruteforce-matrix-corner";
    matrix.appendChild(corner);

    const createMutationNumber = (labelText, value, min = 0, step = 1) => {
      const field = document.createElement("input");
      field.type = "number";
      field.min = String(min);
      field.step = String(step);
      field.className = "bruteforce-field";
      field.value = String(value);
      field.addEventListener("input", () => this.persistSettings());
      field.addEventListener("change", () => this.persistSettings());
      return field;
    };

    const actionFields = {};
    const rowDefinitions = [
      { id: "minCount", label: "Min Count", fieldKey: "minCountField" },
      { id: "maxCount", label: "Max Count", fieldKey: "maxCountField" },
      { id: "minTime", label: "Min Time", fieldKey: "minTimeField" },
      { id: "maxTime", label: "Max Time", fieldKey: "maxTimeField" },
    ];
    if (includeDelta) {
      rowDefinitions.push(
        { id: "deltaMinFrames", label: "Min Offset", fieldKey: "deltaMinField" },
        { id: "deltaMaxFrames", label: "Max Offset", fieldKey: "deltaMaxField" },
      );
    }

    for (const action of BRUTEFORCE_MUTATION_ACTIONS) {
      const actionTitle = document.createElement("div");
      actionTitle.className = `bruteforce-action-heading bruteforce-action-heading-${action.id}`;
      const actionIcon = document.createElement("span");
      actionIcon.className = "bruteforce-action-icon";
      actionIcon.appendChild(createActionIcon(action.id));
      const actionText = document.createElement("span");
      actionText.className = "bruteforce-action-text";
      actionTitle.append(actionIcon, actionText);

      matrix.appendChild(actionTitle);

      const actionSettings = settings[action.id];
      const minCount = createMutationNumber("Min Count", actionSettings.minCount, 0);
      const maxCount = createMutationNumber("Max Count", actionSettings.maxCount, 0);
      const minTime = document.createElement("input");
      minTime.type = "text";
      minTime.className = "bruteforce-field";
      minTime.value = actionSettings.minTime;
      minTime.addEventListener("input", () => this.persistSettings());
      minTime.addEventListener("change", () => this.persistSettings());
      const maxTime = document.createElement("input");
      maxTime.type = "text";
      maxTime.className = "bruteforce-field";
      maxTime.value = actionSettings.maxTime;
      maxTime.addEventListener("input", () => this.persistSettings());
      maxTime.addEventListener("change", () => this.persistSettings());

      let deltaMinField = null;
      let deltaMaxField = null;
      if (includeDelta) {
        deltaMinField = createMutationNumber("Min Frame Offset", actionSettings.deltaMinFrames, -1000000);
        deltaMinField.min = "-1000000";
        deltaMaxField = createMutationNumber("Max Frame Offset", actionSettings.deltaMaxFrames, -1000000);
        deltaMaxField.min = "-1000000";
      }
      actionFields[action.id] = {
        minCountField: minCount,
        maxCountField: maxCount,
        minTimeField: minTime,
        maxTimeField: maxTime,
        deltaMinField,
        deltaMaxField,
      };
    }

    const appendMatrixRow = (labelText, accessor) => {
      const rowLabel = document.createElement("div");
      rowLabel.className = "bruteforce-matrix-label";
      rowLabel.textContent = labelText;
      matrix.appendChild(rowLabel);
      for (const [columnIndex, action] of BRUTEFORCE_MUTATION_ACTIONS.entries()) {
        const field = accessor(action.id);
        const cell = document.createElement("div");
        cell.className = "bruteforce-matrix-cell";
        const rowIndex = rowDefinitions.findIndex((row) => row.label === labelText);
        cell.dataset.mutationKey = key;
        cell.dataset.rowIndex = String(rowIndex);
        cell.dataset.columnIndex = String(columnIndex);
        cell.tabIndex = -1;
        cell.addEventListener("mousedown", (event) => {
          if (event.button !== 0) {
            return;
          }
          this.beginMutationCellSelection(key, rowIndex, columnIndex, event.shiftKey);
        });
        cell.addEventListener("mouseenter", () => {
          if (!this.isSelectingMutationCells) {
            return;
          }
          this.updateMutationCellSelection(key, rowIndex, columnIndex);
        });
        field.addEventListener("focus", () => {
          const current = this.selectedMutationRange;
          if (current?.key === key && current.focusRow === rowIndex && current.focusColumn === columnIndex) {
            return;
          }
          this.beginMutationCellSelection(key, rowIndex, columnIndex, false);
        });
        cell.appendChild(field);
        matrix.appendChild(cell);
      }
    };

    for (const row of rowDefinitions) {
      appendMatrixRow(row.label, (actionId) => actionFields[actionId][row.fieldKey]);
    }

    const matrixHelp = document.createElement("span");
    matrixHelp.className = "bruteforce-help bruteforce-matrix-help";
    matrixHelp.textContent = "Click or drag to select cells, then use Ctrl/Cmd+C and Ctrl/Cmd+V to move blocks between tabs.";

    section.append(heading, help, matrix, matrixHelp);
    this.mutationFieldGroups[key] = {
      actionFields,
      rowDefinitions,
      matrix,
    };
    this.renderMutationSelection();
    return { section };
  }

  beginMutationCellSelection(key, rowIndex, columnIndex, extendSelection) {
    const nextSelection = extendSelection && this.selectedMutationRange?.key === key
      ? {
        ...this.selectedMutationRange,
        focusRow: rowIndex,
        focusColumn: columnIndex,
      }
      : {
        key,
        anchorRow: rowIndex,
        anchorColumn: columnIndex,
        focusRow: rowIndex,
        focusColumn: columnIndex,
      };
    this.selectedMutationRange = nextSelection;
    this.isSelectingMutationCells = true;
    this.renderMutationSelection();
  }

  updateMutationCellSelection(key, rowIndex, columnIndex) {
    if (!this.selectedMutationRange || this.selectedMutationRange.key !== key) {
      return;
    }
    this.selectedMutationRange = {
      ...this.selectedMutationRange,
      focusRow: rowIndex,
      focusColumn: columnIndex,
    };
    this.renderMutationSelection();
  }

  getMutationSelectionBounds(selection = this.selectedMutationRange) {
    if (!selection) {
      return null;
    }
    return {
      top: Math.min(selection.anchorRow, selection.focusRow),
      bottom: Math.max(selection.anchorRow, selection.focusRow),
      left: Math.min(selection.anchorColumn, selection.focusColumn),
      right: Math.max(selection.anchorColumn, selection.focusColumn),
    };
  }

  renderMutationSelection() {
    for (const [key, group] of Object.entries(this.mutationFieldGroups)) {
      const bounds = this.selectedMutationRange?.key === key ? this.getMutationSelectionBounds() : null;
      const cells = group.matrix?.querySelectorAll(".bruteforce-matrix-cell") ?? [];
      for (const cell of cells) {
        const rowIndex = Number(cell.dataset.rowIndex);
        const columnIndex = Number(cell.dataset.columnIndex);
        const isSelected = bounds
          && rowIndex >= bounds.top
          && rowIndex <= bounds.bottom
          && columnIndex >= bounds.left
          && columnIndex <= bounds.right;
        const isAnchor = this.selectedMutationRange?.key === key
          && rowIndex === this.selectedMutationRange.focusRow
          && columnIndex === this.selectedMutationRange.focusColumn;
        cell.classList.toggle("is-selected", Boolean(isSelected));
        cell.classList.toggle("is-selection-anchor", Boolean(isAnchor));
      }
    }
  }

  isMutationClipboardEvent(event) {
    if (!this.selectedMutationRange || !this.root) {
      return false;
    }
    const target = event.target instanceof Element ? event.target : null;
    return Boolean(target?.closest(".bruteforce-mutation-matrix"));
  }

  serializeSelectedMutationCells() {
    const selection = this.selectedMutationRange;
    if (!selection) {
      return "";
    }
    const group = this.mutationFieldGroups[selection.key];
    const bounds = this.getMutationSelectionBounds(selection);
    if (!group || !bounds) {
      return "";
    }
    const lines = [];
    for (let rowIndex = bounds.top; rowIndex <= bounds.bottom; rowIndex += 1) {
      const row = [];
      for (let columnIndex = bounds.left; columnIndex <= bounds.right; columnIndex += 1) {
        const actionId = BRUTEFORCE_MUTATION_ACTIONS[columnIndex]?.id;
        const rowDefinition = group.rowDefinitions[rowIndex];
        const field = actionId && rowDefinition ? group.actionFields[actionId]?.[rowDefinition.fieldKey] : null;
        row.push(field?.value ?? "");
      }
      lines.push(row.join("\t"));
    }
    return lines.join("\n");
  }

  handleMutationMatrixCopy(event) {
    if (!this.isMutationClipboardEvent(event)) {
      return;
    }
    const text = this.serializeSelectedMutationCells();
    if (!text) {
      return;
    }
    event.preventDefault();
    event.clipboardData?.setData("text/plain", text);
  }

  handleMutationMatrixPaste(event) {
    if (!this.isMutationClipboardEvent(event)) {
      return;
    }
    const text = event.clipboardData?.getData("text/plain") ?? "";
    if (!text.trim()) {
      return;
    }
    const selection = this.selectedMutationRange;
    const group = selection ? this.mutationFieldGroups[selection.key] : null;
    const bounds = this.getMutationSelectionBounds(selection);
    if (!group || !bounds) {
      return;
    }
    const rows = text.split(/\r?\n/);
    if (rows.length > 1 && rows[rows.length - 1] === "") {
      rows.pop();
    }
    const parsedRows = rows.map((row) => row.split("\t"));
    event.preventDefault();
    const shouldFillSelection = parsedRows.length === 1 && parsedRows[0].length === 1
      && (bounds.bottom > bounds.top || bounds.right > bounds.left);

    if (shouldFillSelection) {
      const fillValue = parsedRows[0][0] ?? "";
      for (let rowIndex = bounds.top; rowIndex <= bounds.bottom; rowIndex += 1) {
        for (let columnIndex = bounds.left; columnIndex <= bounds.right; columnIndex += 1) {
          const actionId = BRUTEFORCE_MUTATION_ACTIONS[columnIndex]?.id;
          const rowDefinition = group.rowDefinitions[rowIndex];
          const field = actionId && rowDefinition ? group.actionFields[actionId]?.[rowDefinition.fieldKey] : null;
          if (field) {
            field.value = fillValue;
          }
        }
      }
      this.persistSettings();
      return;
    }

    for (const [rowOffset, rowValues] of parsedRows.entries()) {
      const rowIndex = bounds.top + rowOffset;
      const rowDefinition = group.rowDefinitions[rowIndex];
      if (!rowDefinition) {
        continue;
      }
      for (const [columnOffset, value] of rowValues.entries()) {
        const columnIndex = bounds.left + columnOffset;
        const actionId = BRUTEFORCE_MUTATION_ACTIONS[columnIndex]?.id;
        const field = actionId ? group.actionFields[actionId]?.[rowDefinition.fieldKey] : null;
        if (field) {
          field.value = value;
        }
      }
    }
    this.persistSettings();
  }

  renderConditions() {
    if (!this.conditionsList) {
      return;
    }
    this.conditionsList.innerHTML = "";
    this.conditionRows = this.conditionRows.map((entry, index) => {
      const condition = normalizeBruteforceCondition({
        metric: entry.metricField?.value ?? entry.metric,
        type: entry.typeField?.value ?? entry.type,
        value: entry.valueField?.value ?? entry.value,
      });
      const built = this.buildConditionRow(condition, index);
      this.conditionsList.appendChild(built.row);
      return built;
    });
    this.conditionsEmpty.hidden = this.conditionRows.length > 0;
  }

  readConditions() {
    return this.conditionRows.map((entry) => ({
      metric: entry.metricField.value,
      type: entry.typeField.value,
      value: entry.valueField.value.trim(),
    }));
  }

  readSettings() {
    const readMutationGroup = (key) => {
      const group = this.mutationFieldGroups[key];
      const normalizeTimeValue = (value, fallback) => value.trim() || fallback;
      return Object.fromEntries(Object.entries(group.actionFields).map(([id, fields]) => {
        const actionSettings = {
          minCount: Number(fields.minCountField.value || 0),
          maxCount: Number(fields.maxCountField.value || 0),
          minTime: normalizeTimeValue(fields.minTimeField.value, "0:0"),
          maxTime: normalizeTimeValue(fields.maxTimeField.value, "0"),
        };
        if (fields.deltaMinField && fields.deltaMaxField) {
          actionSettings.deltaMinFrames = Number(fields.deltaMinField.value || 0);
          actionSettings.deltaMaxFrames = Number(fields.deltaMaxField.value || 0);
        }
        return [id, actionSettings];
      }));
    };

    return normalizeBruteforceSettings({
      levelId: Number(this.levelField.value || this.settings.levelId || 1),
      target: {
        metric: this.targetMetricField.value,
        direction: this.targetDirectionField.value,
        value: this.targetValueField.value.trim(),
      },
      targetTime: this.targetTimeField.value.trim() || "0:0",
      conditions: this.readConditions(),
      mutations: {
        modify: readMutationGroup("modify"),
        add: readMutationGroup("add"),
        delete: readMutationGroup("delete"),
      },
      scriptText: this.scriptField.value,
    });
  }

  persistSettings() {
    this.settings = {
      ...this.readSettings(),
      scriptText: this.scriptField.value,
    };
    this.onSettingsChange(this.sessionId, this.settings);
    this.updateView();
  }

  handleRunnerUpdate(status) {
    this.status = status;
    if (typeof status.bestScriptText === "string") {
      this.scriptField.value = status.bestScriptText;
      this.settings.scriptText = status.bestScriptText;
      this.onSettingsChange(this.sessionId, this.settings);
    }
    this.updateView();
  }

  updateView() {
    if (!this.root) {
      return;
    }
    const state = this.status?.state ?? "idle";
    const isRunning = state === "running";
    this.root.classList.toggle("is-running", isRunning);
    this.startButton.disabled = isRunning;
    this.stopButton.disabled = !isRunning;
    this.statusMessage.textContent = `State: ${state} - ${this.status?.message ?? (isRunning ? "Running..." : "Ready.")}`;
    this.iterationValue.textContent = `Iterations: ${this.status?.iterations ?? 0}`;
    if (this.status?.bestResult) {
      const targetMetric = getBruteforceMetric(this.settings.target.metric);
      const targetValue = this.status.bestResult.targetValue ?? this.status.bestResult.score;
      const parts = [`${BRUTEFORCE_DIRECTION_LABELS[this.settings.target.direction]} ${targetMetric.shortLabel}: ${targetValue.toFixed(3)}`];
      if (this.settings.target.direction === BRUTEFORCE_DIRECTION_IDS.CONVERGE) {
        const convergeTo = Number(this.settings.target.value);
        if (Number.isFinite(convergeTo)) {
          parts.push(`delta ${Math.abs(targetValue - convergeTo).toFixed(3)}`);
        }
      }
      parts.push(`at ${formatScriptFrame(this.status.bestResult.reachedFrame)}`);
      parts.push(`(${this.status.bestResult.label ?? "best"})`);
      this.bestValue.textContent = `Best: ${parts.join(", ")}`;
    } else {
      const targetMetric = getBruteforceMetric(this.settings.target.metric);
      this.bestValue.textContent = `Best ${BRUTEFORCE_DIRECTION_LABELS[this.settings.target.direction]} ${targetMetric.shortLabel}: --`;
    }
  }

  async start() {
    this.persistSettings();
    await this.runner.start({
      levelId: Number(this.levelField.value || this.getLevelId() || 1),
      baseScriptText: this.scriptField.value,
      spikesKill: this.getSpikesKill(),
      settings: this.settings,
    });
  }

  stop() {
    this.runner.stop();
  }

  dispose() {
    this.stop();
    document.removeEventListener("mouseup", this.handleDocumentMouseUp);
    this.root?.remove();
  }
}
